import { useEffect } from "react";
import { useQuizruns } from "@/hooks/useQuizruns";
import { EventList } from "@/components/events/EventList";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { isUpcomingEvent } from "@/lib/events";
import { isVirtualLocationId } from "@/lib/virtual";
import type { Quizrun } from "@/types/domain";

// SportsEvent JSON-LD for upcoming events — feeds Google/AI "upcoming events"
// widgets that the org-level schema alone can't populate. Omit price entirely
// for pay-what-you-want events; asserting price:0 would falsely signal "free".
function buildEventsJsonLd(quizruns: Quizrun[]): object | null {
  const now = Date.now();
  const events = quizruns
    .filter(isUpcomingEvent)
    .flatMap((qr) =>
      qr.events
        // A quizrun's legs can end at different times (onsite finishes before a
        // later virtual leg); only emit legs that are themselves still upcoming.
        .filter((evt) => new Date(evt.date_finish).getTime() >= now)
        .map((evt) => {
        const loc = evt.location;
        const isVirtual = isVirtualLocationId(evt.location_id);
        const offer =
          evt.price_cents != null && evt.price_cents > 0
            ? {
                "@type": "Offer",
                price: (evt.price_cents / 100).toFixed(2),
                priceCurrency: (evt.price_currency ?? "gbp").toUpperCase(),
                url: `https://quizrun.org/events/${qr.id}`,
                availability: "https://schema.org/InStock",
              }
            : undefined;
        return {
          "@type": "SportsEvent",
          name: qr.title,
          startDate: evt.date_start,
          endDate: evt.date_finish,
          eventAttendanceMode: isVirtual
            ? "https://schema.org/OnlineEventAttendanceMode"
            : "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          url: `https://quizrun.org/events/${qr.id}`,
          description:
            "A hybrid sport combining running with lateral-thinking logic puzzles — correct answers cut your finish time.",
          location: isVirtual
            ? { "@type": "VirtualLocation", url: "https://quizrun.org" }
            : {
                "@type": "Place",
                name: loc?.location_name || loc?.city,
                address: {
                  "@type": "PostalAddress",
                  addressLocality: loc?.city,
                  addressCountry: loc?.country,
                },
              },
          organizer: { "@type": "SportsOrganization", name: "Quizrun", url: "https://quizrun.org" },
          ...(offer ? { offers: offer } : {}),
        };
      }),
    );

  if (events.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: events.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: e,
    })),
  };
}

const Events = () => {
  useEffect(() => {
    document.title = "Events & Results | Quizrun";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Browse past Quizrun results and sign up for upcoming events.");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content = "Browse past Quizrun results and sign up for upcoming events.";
      document.head.appendChild(newMeta);
    }
  }, []);

  const { data: quizruns, isLoading, error } = useQuizruns();

  // Inject upcoming-events schema once data resolves; clean up on unmount/change.
  useEffect(() => {
    if (!quizruns) return;
    const jsonLd = buildEventsJsonLd(quizruns);
    if (!jsonLd) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "events-jsonld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("events-jsonld");
      if (el) el.remove();
    };
  }, [quizruns]);

  return (
    <>
      <PageHeader
        eyebrow="Events Schedule"
        title="All Quizrun Events"
        intro="Browse past results and sign up for upcoming events."
      />

      {/* Events Table Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="sr-only">Events list</h2>
          {isLoading ? (
            <div role="status" aria-label="Loading events" aria-busy="true">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <div className="text-center text-destructive py-8">
              Failed to load events. Please try again later.
            </div>
          ) : (
            <EventList quizruns={quizruns || []} />
          )}
        </div>
      </section>
    </>
  );
};

export default Events;
